import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Mail,
  Plus,
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
  Copy,
  AlertCircle,
  Globe,
  User,
  Eye,
  Check,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'

interface Invitation {
  id: string
  church_name: string
  church_email: string
  church_phone: string
  owner_email: string
  owner_first_name: string
  owner_last_name: string
  owner_phone: string
  invitation_token: string
  status: 'pending' | 'accepted' | 'expired' | 'revoked'
  expires_at: string
  accepted_at?: string
  created_at: string
  invitation_details?: {
    source?: string
    church_address?: string
    church_city?: string
    church_country?: string
    denomination?: string
    estimated_members?: string
    pastor_title?: string
    desired_plan?: string
    hear_about_us?: string
    additional_info?: string
  }
}

interface ChurchInvitationsProps {
  user: AdminUser | null
}

export default function ChurchInvitations({ user }: ChurchInvitationsProps) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [showInviteDialog, setShowInviteDialog] = useState(false)
  const [selectedInvitation, setSelectedInvitation] = useState<Invitation | null>(null)
  const [showDetailsDialog, setShowDetailsDialog] = useState(false)
  const [approvingId, setApprovingId] = useState<string | null>(null)

  const [inviteForm, setInviteForm] = useState({
    church_name: '',
    church_email: '',
    church_phone: '',
    owner_first_name: '',
    owner_last_name: '',
    owner_email: '',
    owner_phone: '',
  })

  // Check if user is super admin
  if (user?.adminLevel !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Access Denied
            </CardTitle>
            <CardDescription>
              You do not have permission to manage church invitations.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  useEffect(() => {
    loadInvitations()
  }, [])

  async function loadInvitations() {
    try {
      setLoading(true)

      const { data, error } = await supabase
        .from('church_invitations')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error

      setInvitations(data || [])
    } catch (error) {
      console.error('Error loading invitations:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handleSendInvitation() {
    try {
      setSending(true)

      // Generate invitation token
      const token = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)

      // Set expiration to 7 days from now
      const expiresAt = new Date()
      expiresAt.setDate(expiresAt.getDate() + 7)

      // Create invitation
      const { data: invitation, error } = await supabase
        .from('church_invitations')
        .insert({
          church_name: inviteForm.church_name,
          church_email: inviteForm.church_email,
          church_phone: inviteForm.church_phone,
          owner_first_name: inviteForm.owner_first_name,
          owner_last_name: inviteForm.owner_last_name,
          owner_email: inviteForm.owner_email,
          owner_phone: inviteForm.owner_phone,
          invitation_token: token,
          invited_by: user!.id,
          expires_at: expiresAt.toISOString(),
          status: 'pending',
        })
        .select()
        .single()

      if (error) throw error

      // Log audit
      await supabase.from('platform_audit_log').insert({
        admin_id: user!.id,
        admin_email: user!.email,
        admin_name: `${user!.firstName} ${user!.lastName}`,
        action: 'church_invitation_sent',
        resource_type: 'church_invitation',
        resource_id: invitation.id,
        new_values: inviteForm,
        description: `Sent invitation to ${inviteForm.owner_email} for ${inviteForm.church_name}`,
      })

      // In a real app, you would send an email here
      // For now, we'll just show the invitation link
      const invitationLink = `${window.location.origin}/accept-invitation/${token}`

      alert(`Invitation created successfully!\n\nInvitation Link:\n${invitationLink}\n\nPlease send this link to ${inviteForm.owner_email}`)

      // Reset form and close dialog
      setInviteForm({
        church_name: '',
        church_email: '',
        church_phone: '',
        owner_first_name: '',
        owner_last_name: '',
        owner_email: '',
        owner_phone: '',
      })
      setShowInviteDialog(false)

      // Reload invitations
      loadInvitations()
    } catch (error: any) {
      console.error('Error sending invitation:', error)
      alert('Failed to send invitation: ' + error.message)
    } finally {
      setSending(false)
    }
  }

  async function handleApproveInvitation(invitation: Invitation) {
    const confirmMessage = `Approve registration for:\n\nChurch: ${invitation.church_name}\nPastor: ${invitation.owner_first_name} ${invitation.owner_last_name}\nEmail: ${invitation.owner_email}\n\nThis will create a new church and admin account.`

    if (!confirm(confirmMessage)) {
      return
    }

    try {
      setApprovingId(invitation.id)

      // Step 1: Create the church
      // Build full address from parts
      const addressParts = [
        invitation.invitation_details?.church_address,
        invitation.invitation_details?.church_city,
        invitation.invitation_details?.church_country
      ].filter(Boolean)
      const fullAddress = addressParts.join(', ')

      const { data: church, error: churchError } = await supabase
        .from('churches')
        .insert({
          name: invitation.church_name,
          email: invitation.church_email,
          phone: invitation.church_phone,
          address: fullAddress || null,
          denomination: invitation.invitation_details?.denomination || null,
          pastor_name: `${invitation.owner_first_name} ${invitation.owner_last_name}`,
          timezone: invitation.invitation_details?.church_country === 'Ghana' ? 'Africa/Accra' : 'Africa/Nairobi',
          settings: {
            desired_plan: invitation.invitation_details?.desired_plan,
            estimated_members: invitation.invitation_details?.estimated_members,
            registration_source: 'website'
          }
        })
        .select()
        .single()

      if (churchError) {
        console.error('Church creation error details:', {
          message: churchError.message,
          details: churchError.details,
          hint: churchError.hint,
          code: churchError.code
        })
        throw churchError
      }

      // Step 2: Check if pastor already has an account
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id, email, church_id, admin_level')
        .eq('email', invitation.owner_email)
        .single()

      let profileMessage = ''

      if (existingProfile) {
        // Update existing profile to make them church owner
        const { error: profileUpdateError } = await supabase
          .from('profiles')
          .update({
            church_id: church.id,
            admin_level: 'church_owner',
            role: 'admin'
          })
          .eq('id', existingProfile.id)

        if (profileUpdateError) {
          console.error('Error updating profile:', profileUpdateError)
          profileMessage = `\n⚠️ Pastor has existing account but auto-upgrade failed. Please manually set:\n- church_id: ${church.id}\n- admin_level: church_owner`
        } else {
          profileMessage = `\n✅ Pastor account upgraded to Church Owner!`
        }
      } else {
        profileMessage = `\n📧 Pastor needs to create an account using: ${invitation.owner_email}\nAfter signup, manually set: church_id = '${church.id}', admin_level = 'church_owner'`
      }

      // Step 3: Update invitation status
      const { error: updateError } = await supabase
        .from('church_invitations')
        .update({
          status: 'accepted',
          church_id: church.id,
          accepted_at: new Date().toISOString(),
        })
        .eq('id', invitation.id)

      if (updateError) throw updateError

      // Step 4: Log audit
      await supabase.from('platform_audit_log').insert({
        admin_id: user!.id,
        admin_email: user!.email,
        admin_name: `${user!.firstName} ${user!.lastName}`,
        action: 'church_registration_approved',
        resource_type: 'church',
        resource_id: church.id,
        description: `Approved registration for ${invitation.church_name}`,
      })

      // Step 5: Show success with church code and next steps
      alert(
        `✅ Church created successfully!\n\n` +
        `📋 Church Details:\n` +
        `- Name: ${church.name}\n` +
        `- Church ID: ${church.id}\n` +
        `- Church Code: ${church.church_code || 'Not generated yet'}\n\n` +
        `👤 Pastor: ${invitation.owner_first_name} ${invitation.owner_last_name}\n` +
        `- Email: ${invitation.owner_email}${profileMessage}\n\n` +
        `📱 Next Steps:\n` +
        `1. Pastor can now login with their email\n` +
        `2. They'll need to use the church code to access this church in the mobile app\n` +
        `3. You can view church details in Church Management`
      )

      loadInvitations()
    } catch (error: any) {
      console.error('Error approving invitation:', error)
      alert('Failed to approve invitation: ' + error.message)
    } finally {
      setApprovingId(null)
    }
  }

  async function handleRevokeInvitation(invitationId: string) {
    if (!confirm('Are you sure you want to revoke this invitation?')) {
      return
    }

    try {
      const { error } = await supabase
        .from('church_invitations')
        .update({ status: 'revoked' })
        .eq('id', invitationId)

      if (error) throw error

      // Log audit
      await supabase.from('platform_audit_log').insert({
        admin_id: user!.id,
        admin_email: user!.email,
        admin_name: `${user!.firstName} ${user!.lastName}`,
        action: 'church_invitation_revoked',
        resource_type: 'church_invitation',
        resource_id: invitationId,
        description: `Revoked invitation`,
      })

      loadInvitations()
    } catch (error: any) {
      console.error('Error revoking invitation:', error)
      alert('Failed to revoke invitation: ' + error.message)
    }
  }

  function copyInvitationLink(token: string) {
    const link = `${window.location.origin}/accept-invitation/${token}`
    navigator.clipboard.writeText(link)
    alert('Invitation link copied to clipboard!')
  }

  function viewInvitationDetails(invitation: Invitation) {
    setSelectedInvitation(invitation)
    setShowDetailsDialog(true)
  }

  function getSourceBadge(invitation: Invitation) {
    const source = invitation.invitation_details?.source
    if (source === 'website_registration') {
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700">
          <Globe className="h-3 w-3 mr-1" />
          Website
        </Badge>
      )
    }
    return (
      <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-300 dark:bg-slate-900/30 dark:text-slate-300 dark:border-slate-700">
        <User className="h-3 w-3 mr-1" />
        Manual
      </Badge>
    )
  }

  function getStatusBadge(status: string, expiresAt: string | null) {
    // Website registrations don't have expiry dates
    const isExpired = expiresAt && new Date(expiresAt) < new Date()

    if (isExpired && status === 'pending') {
      return <Badge className="bg-gray-500"><Clock className="h-3 w-3 mr-1" />Expired</Badge>
    }

    switch (status) {
      case 'accepted':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Accepted</Badge>
      case 'pending':
        return <Badge className="bg-blue-500"><Clock className="h-3 w-3 mr-1" />Pending</Badge>
      case 'revoked':
        return <Badge className="bg-red-500"><XCircle className="h-3 w-3 mr-1" />Revoked</Badge>
      case 'expired':
        return <Badge className="bg-gray-500"><Clock className="h-3 w-3 mr-1" />Expired</Badge>
      default:
        return <Badge variant="outline">Unknown</Badge>
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading invitations...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Church Invitations</h1>
          <p className="text-muted-foreground">Invite new churches to join the platform</p>
        </div>
        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Send Invitation
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Send Church Invitation</DialogTitle>
              <DialogDescription>
                Invite a new church to join the platform. An email will be sent to the church owner
                with setup instructions.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <h4 className="font-semibold">Church Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="church_name">Church Name *</Label>
                    <Input
                      id="church_name"
                      value={inviteForm.church_name}
                      onChange={(e) =>
                        setInviteForm({ ...inviteForm, church_name: e.target.value })
                      }
                      placeholder="First Baptist Church"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="church_email">Church Email *</Label>
                    <Input
                      id="church_email"
                      type="email"
                      value={inviteForm.church_email}
                      onChange={(e) =>
                        setInviteForm({ ...inviteForm, church_email: e.target.value })
                      }
                      placeholder="contact@church.org"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="church_phone">Church Phone</Label>
                    <Input
                      id="church_phone"
                      value={inviteForm.church_phone}
                      onChange={(e) =>
                        setInviteForm({ ...inviteForm, church_phone: e.target.value })
                      }
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold">Church Owner Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="owner_first_name">First Name *</Label>
                    <Input
                      id="owner_first_name"
                      value={inviteForm.owner_first_name}
                      onChange={(e) =>
                        setInviteForm({ ...inviteForm, owner_first_name: e.target.value })
                      }
                      placeholder="John"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="owner_last_name">Last Name *</Label>
                    <Input
                      id="owner_last_name"
                      value={inviteForm.owner_last_name}
                      onChange={(e) =>
                        setInviteForm({ ...inviteForm, owner_last_name: e.target.value })
                      }
                      placeholder="Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="owner_email">Email *</Label>
                    <Input
                      id="owner_email"
                      type="email"
                      value={inviteForm.owner_email}
                      onChange={(e) =>
                        setInviteForm({ ...inviteForm, owner_email: e.target.value })
                      }
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="owner_phone">Phone</Label>
                    <Input
                      id="owner_phone"
                      value={inviteForm.owner_phone}
                      onChange={(e) =>
                        setInviteForm({ ...inviteForm, owner_phone: e.target.value })
                      }
                      placeholder="+1 (555) 987-6543"
                    />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendInvitation} disabled={sending}>
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Invitation
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Invitations Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Invitations</CardTitle>
          <CardDescription>Manage church invitations and track their status</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Church Name</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invitations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No invitations found. Click "Send Invitation" to get started.
                  </TableCell>
                </TableRow>
              ) : (
                invitations.map((invitation) => (
                  <TableRow key={invitation.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{invitation.church_name}</div>
                        <div className="text-sm text-muted-foreground">
                          {invitation.church_email}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {invitation.owner_first_name} {invitation.owner_last_name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {invitation.owner_email}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getSourceBadge(invitation)}</TableCell>
                    <TableCell>{getStatusBadge(invitation.status, invitation.expires_at)}</TableCell>
                    <TableCell>{new Date(invitation.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {invitation.expires_at ? new Date(invitation.expires_at).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {invitation.invitation_details?.source === 'website_registration' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewInvitationDetails(invitation)}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        {invitation.status === 'pending' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleApproveInvitation(invitation)}
                              disabled={approvingId === invitation.id}
                              title="Approve and Create Church"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              {approvingId === invitation.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Check className="h-4 w-4" />
                              )}
                            </Button>
                            {invitation.invitation_token && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyInvitationLink(invitation.invitation_token)}
                                title="Copy Invitation Link"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRevokeInvitation(invitation.id)}
                              title="Revoke Invitation"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Website Registration Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Website Registration Details
            </DialogTitle>
            <DialogDescription>
              Application submitted from the public website
            </DialogDescription>
          </DialogHeader>

          {selectedInvitation && (
            <div className="space-y-6">
              {/* Church Information */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm uppercase text-slate-600 dark:text-slate-400">Church Information</h3>
                <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Church Name</div>
                    <div className="font-medium">{selectedInvitation.church_name}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Email</div>
                    <div className="font-medium">{selectedInvitation.church_email}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Phone</div>
                    <div className="font-medium">{selectedInvitation.church_phone || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Denomination</div>
                    <div className="font-medium">{selectedInvitation.invitation_details?.denomination || 'N/A'}</div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-xs text-slate-500 mb-1">Address</div>
                    <div className="font-medium">
                      {selectedInvitation.invitation_details?.church_address || 'N/A'}
                      {selectedInvitation.invitation_details?.church_city && `, ${selectedInvitation.invitation_details.church_city}`}
                      {selectedInvitation.invitation_details?.church_country && `, ${selectedInvitation.invitation_details.church_country}`}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Number of Members</div>
                    <div className="font-medium">{selectedInvitation.invitation_details?.estimated_members || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Pastor Information */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm uppercase text-slate-600 dark:text-slate-400">Pastor Information</h3>
                <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Name</div>
                    <div className="font-medium">
                      {selectedInvitation.invitation_details?.pastor_title} {selectedInvitation.owner_first_name} {selectedInvitation.owner_last_name}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Email</div>
                    <div className="font-medium">{selectedInvitation.owner_email}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Phone</div>
                    <div className="font-medium">{selectedInvitation.owner_phone || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Plan Selection */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm uppercase text-slate-600 dark:text-slate-400">Desired Plan</h3>
                <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <div className="font-medium capitalize">{selectedInvitation.invitation_details?.desired_plan || 'N/A'}</div>
                </div>
              </div>

              {/* Additional Information */}
              {(selectedInvitation.invitation_details?.hear_about_us || selectedInvitation.invitation_details?.additional_info) && (
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm uppercase text-slate-600 dark:text-slate-400">Additional Information</h3>
                  <div className="space-y-3 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                    {selectedInvitation.invitation_details?.hear_about_us && (
                      <div>
                        <div className="text-xs text-slate-500 mb-1">How they heard about us</div>
                        <div className="font-medium capitalize">{selectedInvitation.invitation_details.hear_about_us}</div>
                      </div>
                    )}
                    {selectedInvitation.invitation_details?.additional_info && (
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Additional Notes</div>
                        <div className="font-medium">{selectedInvitation.invitation_details.additional_info}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Submission Date */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="text-sm">
                  <strong>Submitted:</strong> {new Date(selectedInvitation.created_at).toLocaleString()}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
